/**
 * The per-row action buttons in the PO Lines grid: acknowledge / accept,
 * decline, review changes, propose or update dates, and hold or release.
 *
 * Lifted out of POLineRow at gate round 1 (comments-C3): this item's work took
 * that file from 491 lines to 535, past helix-code's 500-line cap, and the
 * action cell is the part of it that is one coherent thing.
 *
 * It decides NOTHING about who may do what — every predicate arrives as a prop,
 * from usePOLinesState and from `canReleaseHold`, which is the shared rule the
 * server mirrors. This renders the controls those answers allow.
 */
import { Check, XCircle, Pause, Play, Edit, FileSearch } from 'lucide-react';
import type { PurchaseOrderLine } from './types';
import type { User } from '../../../../lib/api';
import { canReleaseHold, lineIsFinished } from './utils';
import OTIFReviewActions from '../../OTIFReviewActions';

interface POLineRowActionsProps {
  line: PurchaseOrderLine;
  user: User | null;
  /** Two or more rows are selected: per-row controls render disabled. */
  bulkMode: boolean;
  bulkModeTitle: string;
  shouldShowActions: (line: PurchaseOrderLine) => boolean;
  shouldShowDecline: (line: PurchaseOrderLine) => boolean;
  shouldShowProposeChange: (line: PurchaseOrderLine) => boolean;
  onReviewChanges: (lineId: number) => void;
  onSelectLineForWorkflow: (line: PurchaseOrderLine, modal: 'accept' | 'decline') => void;
  onProposeChange: (line: PurchaseOrderLine) => void;
  onPutOnHold: (line: PurchaseOrderLine) => void;
  onReleaseHold: (line: PurchaseOrderLine) => void;
  /**
   * CR-7418: the OTIF Review pill is the active tab, so this row is one the
   * buyer is here to decide. Gated on the pill rather than on the line's own
   * `can_*` flags, which is what legacy does — the decision belongs to the
   * review queue, not to every appearance of a delivered line.
   */
  otifReview?: boolean;
  /** Refetch the list (and with it the pill's count) after a decision. */
  onOtifDone?: () => void;
}

export function POLineRowActions({
  line,
  user,
  bulkMode,
  bulkModeTitle,
  shouldShowActions,
  shouldShowDecline,
  shouldShowProposeChange,
  onReviewChanges,
  onSelectLineForWorkflow,
  onProposeChange,
  onPutOnHold,
  onReleaseHold,
  otifReview = false,
  onOtifDone,
}: POLineRowActionsProps) {
  const isCustomerUser = user?.platform === 'CUSTOMER' || user?.platform === 'HELIX';
  const hasChangeProposed = line.acknowledge_status === 'change_proposed_supplier'
    || line.acknowledge_status === 'change_proposed_customer';

  // CR-7418: on the review pill the row's job is the OTIF decision, and the
  // negotiation controls do not apply to it anyway — every line in this queue
  // is `received`. Showing only the decision keeps the column readable.
  // Hold controls too: Release lives on the line's other tabs (CR-6348, Aaron
  // 5 Oct 2026 — this column carries the OTIF review actions and nothing else,
  // as legacy's OTIF Review sub-tab does).
  if (otifReview) {
    return (
      <div className="flex items-center justify-center gap-1">
        <OTIFReviewActions
          variant="icon"
          line={line}
          disabled={bulkMode}
          onDone={() => onOtifDone?.()}
        />
      </div>
    );
  }

  return (
      <div className="flex items-center justify-center gap-2">
        {/* CR-4728: Customer users see a single "Review Changes" icon for proposed changes.
            Opens the PO Line popup directly on the Change Requests tab. */}
        {isCustomerUser && hasChangeProposed ? (
          <button onClick={(e) => { e.stopPropagation(); onReviewChanges(line.id); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-amber-50'}`} title={bulkMode ? bulkModeTitle : 'Review Changes'}>
            <FileSearch className="h-5 w-5 text-amber-600" />
          </button>
        ) : (
          <>
            {shouldShowActions(line) && (
              <>
                {/* EXPEDITING-PARITY PR 2 — the control says what it does
                    (Aaron: "acknowledge button on the PO line grid").
                    On a `new` line the supplier is ACKNOWLEDGING the
                    order; the same button on a change_proposed_* line is
                    ACCEPTING the counterparty's proposal. One icon, two
                    different things, and the tooltip is where a user
                    finds out which. */}
                <button onClick={(e) => { e.stopPropagation(); onSelectLineForWorkflow(line, 'accept'); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-green-50'}`} title={bulkMode ? bulkModeTitle : (line.acknowledge_status === 'new' ? 'Acknowledge' : 'Accept')}>
                  <Check className="h-5 w-5 text-green-600" />
                </button>
                {shouldShowDecline(line) && (
                  <button onClick={(e) => { e.stopPropagation(); onSelectLineForWorkflow(line, 'decline'); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-50'}`} title={bulkMode ? bulkModeTitle : 'Decline'}>
                    <XCircle className="h-5 w-5 text-red-600" />
                  </button>
                )}
              </>
            )}
            {/* Walkthrough batch 3 (Aaron, 17 Sep 2026): one control,
                and the STATUS picks what it opens — on a customer's `new`
                line this goes to Update Dates (a direct write, P10), not
                to a proposal. The modal does the routing; the tooltip has
                to say the same thing or the button lies about itself.
                Everything else this tooltip depends on is already decided
                by shouldShowProposeChange (write access, schedule-grain
                tenants, held and received lines all return false). */}
            {shouldShowProposeChange(line) && (
              <button onClick={(e) => { e.stopPropagation(); onProposeChange(line); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-50'}`} title={bulkMode ? bulkModeTitle : (isCustomerUser && line.acknowledge_status === 'new' ? 'Update Dates' : 'Propose Change')}>
                <Edit className="h-5 w-5 text-blue-600" />
              </button>
            )}
          </>
        )}
        {/* CR-7418 round 7 (Aaron, 19 Sep): a CLOSED line offers nothing.
            CR-4697 already ruled "no actions on closed (fully received) PO
            lines" and shouldShowActions/shouldShowProposeChange both honour
            it — but the hold control sits outside that branch (see below) and
            so survived, which is why a fully received line still offered Put
            On Hold. The server does not refuse it either: put_on_hold checks
            the reason and whether the line is ALREADY held, not its status. So
            this was a control that worked and should not have existed.

            A CANCELLED line is now treated the same (Aaron, 19 Sep: *"yes
            cancelled line is ok"*). It was left out of the first cut because
            shouldShowActions did not guard it either and fixing one without
            the other leaves the two disagreeing — so both are guarded now, in
            one place each, and `LINE_IS_CLOSED` is the single predicate.

            EXPEDITING-PARITY PR 2 — Aaron, 16 Sep: "on hold button should
            be in the action column". It sits OUTSIDE the branch above
            because holding a line is orthogonal to the negotiation: a
            customer reviewing a proposed change can still hold it, and a
            held line keeps its release control even though every other
            control is gone. Only the side that placed the hold gets the
            release — a disabled button reads as "broken", so the other
            side sees the STATUS badge, which reads "On Hold (Customer)" or
            "(Supplier)" and names the side that can lift it, and nothing here.
            (It used to say "the On Hold column's indicator"; that column
            retired into the status badge under the same ruling, which is the
            kind of comment the diff's own change makes false.)

            Consequence worth knowing: a tenant that hides the Action
            column for suppliers (CR-5884 hide_actions) now hides their
            hold control with it. That is consistent with what the switch
            means — this side does not act on lines here — and it is the
            price of the control living where every other action lives. */}
        {/* ROUND 2: the finished-line guard belongs on PUT ON HOLD, not on
            RELEASE. Wrapping both branches trapped a line that was held and
            then received: the hold survives the receipt (nothing clears it),
            the grid offered no Release, the modal already refused one
            (canToggleHold, `status !== 'received'`) and the server never
            refused either — so the only way out was the database. Measured on
            the CR-7418 clone at round 2: two lines sat in exactly that state.
            A finished line should not be newly held; one already held must
            always be releasable. */}
        {line.on_hold ? (
          canReleaseHold(user, line) && (
            <button
              onClick={(e) => { e.stopPropagation(); onReleaseHold(line); }}
              className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-green-50'}`}
              title={bulkMode ? bulkModeTitle : 'Release from hold'} disabled={bulkMode}
            >
              <Play className="h-5 w-5 text-green-700" />
            </button>
          )
        ) : !lineIsFinished(line) && (
          <button onClick={(e) => { e.stopPropagation(); onPutOnHold(line); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-orange-50'}`} title={bulkMode ? bulkModeTitle : 'Put on hold'}>
            <Pause className="h-5 w-5 text-orange-600" />
          </button>
        )}
      </div>
  );
}
